import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Space, Spin, Tag, message } from "antd";
import dayjs from "dayjs";
import { GenericStatusPill } from "../shared/statusColors";
import { DayApprovalState, dailyApprovalApis, normaliseReview, normaliseState } from "../../../api/DailyApprovals";

// Shared pieces of Daily Execution Approvals: the status pill, the remarks
// dialog every transition uses, and the submit dialog with the day summary.

const TONES: Record<string, { bg: string; text: string; dot: string }> = {
  none: { bg: "#eef1f6", text: "#526077", dot: "#8094ae" },
  blue: { bg: "#e7eefc", text: "#2557d6", dot: "#2557d6" },
  violet: { bg: "#efeafb", text: "#6a45c2", dot: "#6a45c2" },
  green: { bg: "#e3f3ea", text: "#1d7a4c", dot: "#1d7a4c" },
  red: { bg: "#f9e5e5", text: "#b23131", dot: "#b23131" },
  amber: { bg: "#fbf0dc", text: "#9a5d06", dot: "#c98a1a" },
};

export const approvalLabel = (state: Pick<DayApprovalState, "status" | "currentLevel" | "levelCount">): string => {
  switch (state.status) {
    // "of N" matters now that a chain can be any depth — level 2 of 2 is
    // nearly done, level 2 of 4 is not.
    case "Submitted":
      return state.levelCount > 0
        ? `Waiting for level ${state.currentLevel || 1} of ${state.levelCount}`
        : "Submitted · locked";
    case "InReview":
      return `Waiting for level ${state.currentLevel} of ${state.levelCount}`;
    case "Approved":
      return "Approved";
    case "Rejected":
      return "Rejected";
    case "Recalled":
      return "Recalled";
    case "Reopened":
      return "Reopened";
    default:
      return "Not submitted";
  }
};

const toneOf = (status: string) =>
  status === "Submitted" ? TONES.blue
    : status === "InReview" ? TONES.violet
      : status === "Approved" ? TONES.green
        : status === "Rejected" ? TONES.red
          : status === "Recalled" || status === "Reopened" ? TONES.amber
            : TONES.none;

export const ApprovalStatusPill: React.FC<{
  state: Pick<DayApprovalState, "status" | "currentLevel" | "levelCount">;
}> = ({ state }) => <GenericStatusPill label={approvalLabel(state)} tone={toneOf(state.status)} />;

export const fmtDay = (iso: string) => (iso ? dayjs(iso).format("DD-MMM-YYYY") : "—");
export const fmtWhen = (iso: string) => (iso ? dayjs(iso).format("DD-MMM-YYYY HH:mm") : "");
export const fmtMoney = (value: string | number) => {
  if (value === "" || value === null || value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n)
    ? `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
};
export const perUnitLabel = (uom: string) => (/cubi[ct]\s*met/i.test(uom || "") ? "Cum" : uom || "unit");

/** One dialog for approve / reject / reopen / recall — remarks with a minimum length. */
export const RemarksModal: React.FC<{
  open: boolean;
  title: string;
  intro?: React.ReactNode;
  okText: string;
  danger?: boolean;
  minLength: number;
  placeholder: string;
  onCancel: () => void;
  onSubmit: (remarks: string) => Promise<boolean>;
}> = ({ open, title, intro, okText, danger, minLength, placeholder, onCancel, onSubmit }) => {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setRemarks("");
  }, [open]);
  const trimmed = remarks.trim();
  const short = trimmed.length < minLength;
  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="ok"
          type="primary"
          danger={danger}
          loading={busy}
          disabled={short}
          onClick={async () => {
            setBusy(true);
            try {
              if (await onSubmit(trimmed)) onCancel();
            } finally {
              setBusy(false);
            }
          }}
        >
          {okText}
        </Button>,
      ]}
    >
      {intro && <div style={{ marginBottom: 12 }}>{intro}</div>}
      <Input.TextArea
        id="daily-approval-remarks"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        rows={4}
        maxLength={2000}
        placeholder={placeholder}
        autoFocus
      />
      {minLength > 0 && (
        <div className="pms-appr-hint">
          {short ? `At least ${minLength} characters (${trimmed.length} so far)` : "Ready"}
        </div>
      )}
    </Modal>
  );
};

/** Submit a site day: shows what is being submitted and anything that needs a look. */
export const SubmitDayModal: React.FC<{
  open: boolean;
  projectId: number | string;
  reportDate: string;
  onCancel: () => void;
  onDone: (state: DayApprovalState) => void;
}> = ({ open, projectId, reportDate, onCancel, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState<ReturnType<typeof normaliseReview> | null>(null);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !projectId || !reportDate) return;
    let cancelled = false;
    setRemarks("");
    setReview(null);
    setLoading(true);
    dailyApprovalApis
      .review(projectId, reportDate)
      .then((res) => !cancelled && res?.status === true && setReview(normaliseReview(res.data)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, projectId, reportDate]);

  const summary = review?.summary;
  const waits = (review?.state.levelCount ?? 0) > 0;
  return (
    <Modal
      open={open}
      title={`Submit ${fmtDay(reportDate)}`}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={busy}
          disabled={loading || !summary || summary.faceCount === 0}
          onClick={async () => {
            setBusy(true);
            try {
              const res = await dailyApprovalApis.submit({
                projectId: Number(projectId),
                reportDate,
                remarks: remarks.trim(),
              });
              if (res?.status !== true) {
                message.error(res?.message || "Could not submit the day");
                return;
              }
              message.success(res.data?.message || "Day submitted");
              onDone(normaliseState(res.data?.state));
            } finally {
              setBusy(false);
            }
          }}
        >
          Submit for approval
        </Button>,
      ]}
    >
      {loading || !summary ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div className="pms-appr-submit-grid">
            <div><b>{summary.faceCount}</b><span>faces</span></div>
            <div><b>{summary.boqLineCount}</b><span>BOQ lines</span></div>
            <div><b>{summary.materialTaskDayCount}</b><span>material task-days</span></div>
            <div><b>{summary.machineCount}</b><span>machines</span></div>
            <div><b>{summary.stockMaterialCount}</b><span>store materials</span></div>
          </div>
          {/* A day is approved as a whole, so Civil is no longer required on its
              own — only a completely empty day cannot be submitted. */}
          {summary.faceCount === 0 &&
            summary.materialTaskDayCount === 0 &&
            summary.machineCount === 0 &&
            summary.stockMaterialCount === 0 && (
              <Alert
                type="warning"
                showIcon
                message="Nothing is recorded for this day yet — enter Civil, Material-Stores, Mechanical or Store-Stock before submitting."
              />
            )}
          {summary.flagCount > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${summary.flagCount} item(s) will be flagged for the reviewer`}
              description={
                <ul className="pms-appr-flag-list">
                  {review!.flags.slice(0, 5).map((flag, i) => (
                    <li key={i}>
                      <Tag color={flag.severity === "high" ? "red" : "gold"}>{flag.title}</Tag> {flag.detail}
                    </li>
                  ))}
                  {review!.flags.length > 5 && <li>and {review!.flags.length - 5} more</li>}
                </ul>
              }
            />
          )}
          <div className="pms-appr-hint">
            {waits
              ? "The day locks and goes to the level 1 approvers. It opens again only if it is recalled or rejected."
              : "No approvers are set up for this project, so submitting only locks the day."}
          </div>
          <Input.TextArea
            id="daily-approval-submit-remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Note for the reviewer (optional)"
          />
        </Space>
      )}
    </Modal>
  );
};
